(() => {
  const ROOT_SELECTOR = '[data-product-size-guide]';
  const EIGHTH_FRACTIONS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
  const FRACTION_VALUES = {
    '⅛': 0.125,
    '¼': 0.25,
    '⅜': 0.375,
    '½': 0.5,
    '⅝': 0.625,
    '¾': 0.75,
    '⅞': 0.875,
  };

  const parseSingleMeasurement = (rawValue) => {
    const normalised = String(rawValue ?? '')
      .trim()
      .replace(',', '.')
      .replace(/\s+/g, '');

    if (!normalised) return Number.NaN;

    const decimalValue = Number(normalised);
    if (Number.isFinite(decimalValue)) return decimalValue;

    const fractionMatch = normalised.match(/^([+-]?\d+)?([⅛¼⅜½⅝¾⅞])$/);
    if (!fractionMatch) return Number.NaN;

    const whole = fractionMatch[1] ? Number(fractionMatch[1]) : 0;
    const fraction = FRACTION_VALUES[fractionMatch[2]];
    return whole < 0 ? whole - fraction : whole + fraction;
  };

  const parseMeasurement = (rawValue) => {
    const source = String(rawValue ?? '').trim();
    if (!source) return [];

    const normalisedRange = source
      .replace(/\bto\b/gi, '–')
      .replace(/[—−]/g, '–');

    let parts = normalisedRange.split(/\s*–\s*/);

    if (parts.length === 1 && /^\s*\d+(?:[.,]\d+)?\s*-\s*\d+(?:[.,]\d+)?\s*$/.test(source)) {
      parts = source.split(/\s*-\s*/);
    }

    const values = parts.map(parseSingleMeasurement);
    return values.every(Number.isFinite) ? values : [];
  };

  const formatEighthInches = (value) => {
    const eighthCount = Math.round((value + Number.EPSILON) * 8);
    const whole = Math.floor(eighthCount / 8);
    const remainder = eighthCount % 8;
    return `${whole}${EIGHTH_FRACTIONS[remainder]}`;
  };

  const formatHalfInches = (value) => {
    const halfCount = Math.round((value + Number.EPSILON) * 2);
    const whole = Math.floor(halfCount / 2);
    const remainder = halfCount % 2;
    return `${whole}${remainder ? '½' : ''}`;
  };

  const formatCentimetres = (value) => {
    const rounded = Math.round((value + Number.EPSILON) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const convertMeasurement = (value, sourceUnit, targetUnit) => {
    if (sourceUnit === 'cm' && targetUnit === 'in') return value / 2.54;
    if (sourceUnit === 'in' && targetUnit === 'cm') return value * 2.54;
    return value;
  };

  const formatMeasurement = (rawValue, sourceUnit, targetUnit, measurementKey) => {
    const values = parseMeasurement(rawValue);
    if (!values.length) return null;

    return values
      .map((value) => {
        const converted = convertMeasurement(value, sourceUnit, targetUnit);

        if (targetUnit !== 'in') return formatCentimetres(converted);

        return measurementKey === 'collar'
          ? formatHalfInches(converted)
          : formatEighthInches(converted);
      })
      .join('–');
  };

  const initialiseGuide = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.sizeGuideInitialised === 'true') return;

    const sourceUnit = root.dataset.sourceUnit === 'in' ? 'in' : 'cm';
    const tabs = Array.from(root.querySelectorAll('[data-size-guide-unit]'));
    const cells = Array.from(root.querySelectorAll('[data-size-guide-value]'));
    const unitLabel = root.querySelector('[data-size-guide-unit-label]');
    const unitNote = root.querySelector('.product-size-guide__unit-note');
    const panel = root.querySelector('[data-size-guide-panel]');
    const caption = root.querySelector('caption');

    if (!tabs.length || !cells.length) return;

    const setUnit = (targetUnit, moveFocus = false) => {
      const safeUnit = targetUnit === 'cm' ? 'cm' : 'in';
      const activeTab = tabs.find((tab) => tab.dataset.sizeGuideUnit === safeUnit);

      tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      cells.forEach((cell) => {
        const formattedValue = formatMeasurement(
          cell.dataset.sourceValue,
          sourceUnit,
          safeUnit,
          cell.dataset.measurementKey
        );
        if (formattedValue !== null) cell.textContent = formattedValue;
      });

      if (unitLabel) unitLabel.textContent = safeUnit === 'cm' ? 'centimetres' : 'inches';
      if (unitNote) {
        unitNote.lastChild.textContent =
          safeUnit === 'in'
            ? '. Collar/size values are rounded to the nearest ½ inch; all other inch measurements are rounded to the nearest ⅛ inch.'
            : '.';
      }
      if (caption) {
        const baseCaption = caption.textContent.replace(/\s+in (inches|centimetres)\s*$/i, '').trim();
        caption.textContent = `${baseCaption} in ${safeUnit === 'cm' ? 'centimetres' : 'inches'}`;
      }
      if (panel && activeTab) panel.setAttribute('aria-labelledby', activeTab.id);
      if (moveFocus && activeTab) activeTab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setUnit(tab.dataset.sizeGuideUnit));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else return;

        event.preventDefault();
        setUnit(tabs[nextIndex].dataset.sizeGuideUnit, true);
      });
    });

    root.dataset.sizeGuideInitialised = 'true';
    setUnit('in');
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialiseGuide(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialiseGuide);
  };

  window.LivingstonSizeGuides = { initialiseAll };
  initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
